// src/components/admin/AdminHeader.jsx  
import React, { useState, useRef, useEffect } from "react";  
import { useNavigate } from "react-router-dom";  
import { Menu, Search, LogOut, ChevronDown, Shield } from "lucide-react";  
import { motion, AnimatePresence } from "framer-motion";  
import SystemHealthPill from "./SystemHealthPill";  
import api from "../../api";  
  
const AdminHeader = ({ toggleSidebar, handleLogout, isMobile }) => {  
  const navigate = useNavigate();  
  const [showProfile, setShowProfile] = useState(false);  
  const [searchQuery, setSearchQuery] = useState("");  
  const [admin, setAdmin] = useState({ username: "Admin", email: "" });  
  const profileRef = useRef(null);  
  
  useEffect(() => {  
    let mounted = true;  
    api.get("/users/me")  
      .then((res) => {  
        if (mounted && res.data) {  
          setAdmin({  
            username: res.data.username || "Admin",  
            email: res.data.email || "",  
          });  
        }  
      })  
      .catch(() => {});  
    return () => { mounted = false; };  
  }, []);  
  
  useEffect(() => {  
    const handleClickOutside = (e) => {  
      if (profileRef.current && !profileRef.current.contains(e.target)) {  
        setShowProfile(false);  
      }  
    };  
    document.addEventListener("mousedown", handleClickOutside);  
    return () => document.removeEventListener("mousedown", handleClickOutside);  
  }, []);  
  
  const handleSearch = (e) => {  
    e.preventDefault();  
    if (searchQuery.trim()) {  
      navigate(`/admin/users?search=${encodeURIComponent(searchQuery.trim())}`);  
      setSearchQuery("");  
    }  
  };  
  
  const initial = admin.username?.charAt(0).toUpperCase() || "A";  
  
  return (  
    <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-xl border-b border-gray-800">  
      <div className="flex items-center justify-between h-14 px-4 md:px-6">  
        {/* Left */}  
        <div className="flex items-center gap-3 flex-1 min-w-0">  
          {isMobile && (  
            <button  
              onClick={toggleSidebar}  
              className="p-2 rounded-lg hover:bg-white/[0.03] transition-colors"  
            >  
              <Menu size={20} className="text-gray-400" />  
            </button>  
          )}  
  
          <form onSubmit={handleSearch} className="hidden md:block max-w-sm w-full">  
            <div className="relative">  
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />  
              <input  
                type="text"  
                value={searchQuery}  
                onChange={(e) => setSearchQuery(e.target.value)}  
                placeholder="Search users…"  
                className="w-full pl-9 pr-3 py-2 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all"  
              />  
            </div>  
          </form>  
        </div>  
  
        {/* Right */}  
        <div className="flex items-center gap-2">  
          {/* System health — always visible in header */}  
          <SystemHealthPill />  
  
          {/* Mobile search shortcut */}  
          <button  
            onClick={() => navigate("/admin/users")}  
            className="md:hidden p-2 rounded-lg hover:bg-white/[0.03] transition-colors"  
            title="Search users"  
          >  
            <Search size={18} className="text-gray-400" />  
          </button>  
  
          {/* Profile */}  
          <div ref={profileRef} className="relative">  
            <button  
              onClick={() => setShowProfile(!showProfile)}  
              className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-lg border border-transparent hover:border-gray-800 hover:bg-white/[0.03] transition-all"  
            >  
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-gold to-yellow-600 flex items-center justify-center flex-shrink-0">  
                <span className="text-black font-bold text-xs">{initial}</span>  
              </div>  
              <div className="hidden md:block text-left min-w-0">  
                <p className="text-xs font-semibold text-white truncate max-w-[120px]">{admin.username}</p>  
                <p className="text-[10px] text-gray-500">Administrator</p>  
              </div>  
              <ChevronDown size={14} className="text-gray-500 hidden md:block" />  
            </button>  
  
            <AnimatePresence>  
              {showProfile && (  
                <motion.div  
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}  
                  animate={{ opacity: 1, y: 0, scale: 1 }}  
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}  
                  transition={{ duration: 0.15 }}  
                  className="absolute right-0 mt-2 w-64 bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-xl shadow-2xl overflow-hidden"  
                >  
                  <div className="p-3 border-b border-gray-800">  
                    <div className="flex items-center gap-3">  
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gold to-yellow-600 flex items-center justify-center">  
                        <span className="text-black font-bold text-sm">{initial}</span>  
                      </div>  
                      <div className="min-w-0">  
                        <p className="text-sm font-semibold text-white truncate">{admin.username}</p>  
                        <p className="text-[11px] text-gray-500 truncate">{admin.email || "—"}</p>  
                      </div>  
                    </div>  
                    <div className="mt-3 flex items-center gap-1.5 px-2 py-1 bg-gold/10 border border-gold/30 rounded text-[10px] font-semibold text-gold uppercase tracking-wider w-fit">  
                      <Shield size={10} />  
                      Super admin  
                    </div>  
                  </div>  
                  <div className="p-1.5">  
                    <button  
                      onClick={handleLogout}  
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-red-500/[0.06] transition-colors text-red-400 text-sm"  
                    >  
                      <LogOut size={15} />  
                      Sign out  
                    </button>  
                  </div>  
                </motion.div>  
              )}  
            </AnimatePresence>  
          </div>  
        </div>  
      </div>  
    </header>  
  );  
};  
  
export default AdminHeader;  